import React, { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { Box, Image, Text } from "@nubeteck/components";
import { RegisterSVG as IntroductionImg } from "../../../assets";
import {
  LargeHeadingStyled,
  SmallHeadingStyled,
} from "../../../components/custom/texts/texts.styled";
import { CardStyled as RegisterCard } from "../../../components/custom/cards/card.styled";
import { WarningCard } from "../../custom/cards";
import {
  CareersOptionsContainer,
  FlexStyled,
  IntroductionContainer,
  OptionsContainer,
  RadioGroupStyled,
} from "./register-introduction.styled";
import { ICareer } from "@/Interfaces/career";
import { Checkbox, Skeleton } from "antd";
import { useSelector } from "react-redux";
import { RootState } from "src/core/store/store";
import { FormatterUtils } from "@nubeteck/utils";
import parse from "html-react-parser";
import { getEnrollmentPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../../pages/StrapiError";
import { _translateValue } from "../../../pages/admin-pages/translation-gestor/translateValue";
import { formatDate } from "src/utils/date-util";

const SCREEN_NAME = "PantallaRegisterIntroduction";

const RegisterIntroduction = ({
  careers,
  setCareer,
  setPeriod,
  isLoading,
  timeValue,
}: {
  careers: ICareer[];
  setCareer: (id: number) => void;
  setPeriod: (id: number) => void;
  isLoading: boolean;
  goNext: () => void;
  timeValue: number;
}) => {
  const { userInfo } = useSelector((state: RootState) => state.user);

  const { setValue, getValues } = useFormContext();
  const [terminos, setTerminos] = useState<string>("");
  const [careerValue, setCareerValue] = useState<{
    carreraId: number;
    seleccionSinTerminar: ICareer | undefined;
    periodoId: number;
  }>();
  const { locale } = useLocale();
  const [enrollmentPage, setEnrollmentPage] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [translatedTerminos, setTranslatedTerminos] = useState<string>("");
  useEffect(() => {
    if (careers?.length > 0)
      setCareerValue({
        carreraId: careers?.filter((career) => career.periodo !== null)[0]?.carreraId,
        seleccionSinTerminar: careers?.find((c) => c.tieneSeleccionSinTerminar === true),
        periodoId: careers?.filter((career) => career.periodo !== null)[0]?.periodoId,
      });
  }, [careers]);

  useEffect(() => {
    if (careerValue?.carreraId) {
      setValue("programa", careerValue);
      setCareer(careerValue.carreraId);
      setPeriod(careerValue.periodoId);
      setTerminos(careers.find((c) => c.carreraId === careerValue.carreraId)!.terminosYCondiciones);
      setValue("esR3", careers.find((c) => c.carreraId === careerValue.carreraId)!.esR3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [careerValue]);

  // Efecto para traducir los términos y condiciones cuando cambien o cambie el locale
  useEffect(() => {
    const translateTerminos = async () => {
      if (!terminos || terminos.trim() === "") {
        setTranslatedTerminos("");
        return;
      }

      try {
        const translated = await _translateValue(SCREEN_NAME, terminos, locale);
        setTranslatedTerminos(translated);
      } catch (error) {
        console.error("Error al traducir términos y condiciones:", error);
        setTranslatedTerminos(terminos);
      }
    };
    translateTerminos();
  }, [terminos, locale]);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setEnrollmentPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    getEnrollmentPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setEnrollmentPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setEnrollmentPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  if (hasError && locale === 'en') {
    return <StrapiError
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setEnrollmentPage(null);
      }}
    />;
  }

  return (
    <RegisterCard>
      <IntroductionContainer>
        <Box>
          <Skeleton loading={isLoading} active round />
          {!isLoading && (
            <>
              <LargeHeadingStyled>
                {isLoadingContent ? (
                  <Skeleton.Input active size="large" style={{ width: 300 }} />
                ) : (
                  enrollmentPage?.title || "Estimado(a) estudiante:"
                )}
              </LargeHeadingStyled>
              <SmallHeadingStyled>
                {isLoadingContent ? (
                  <Skeleton.Input active size="default" style={{ width: 400 }} />
                ) : (
                  enrollmentPage?.subtitleV || "Bienvenido(a) al proceso de matrícula"
                )}
              </SmallHeadingStyled>
              <Text style={{ fontWeight: "bold" }}>
              {enrollmentPage?.paragraph1 || "Antes de iniciar, es muy importante que tenga presente lo siguiente:"}
                <p>
                1. {enrollmentPage?.pagraph2 || "Tendrá un tiempo máximo de "} {timeValue} {enrollmentPage?.pagraph1_2 || "minutos para completar el proceso de matrícula."}

                </p>
                <p>
                2. {enrollmentPage?.pagraph3 || "Si el tiempo se agota o sale del sistema antes de completar el proceso de matrícula, todas las operaciones realizadas serán reversadas por lo que usted no quedará inscrito en ninguno de los cursos que haya seleccionado."}

                </p>
                <p>3. {enrollmentPage?.pagraph4 || "El sistema sólo permite el pago por medio de tarjetas de crédito / débito."}</p>
              </Text>

              <SmallHeadingStyled style={{ marginBottom: 0 }}>
                {" "}
                {careers?.filter((career) => career.periodo !== null).length > 0
                ? enrollmentPage?.messageO || "A continuación elige el programa a matricular:"
                : enrollmentPage?.messageT || "No tiene programas con asignaturas disponibles para seleccionar."}
                  
              </SmallHeadingStyled>
              <FlexStyled>
                <OptionsContainer>
                  <RadioGroupStyled
                    style={{ flexDirection: "column", alignItems: "flex-start" }}
                    onChange={(e: any) => {
                      setCareerValue({
                        carreraId: e.target.value,
                        periodoId:
                          careers.find((c) => c.carreraId === e.target.value)?.periodoId ?? 0,
                        seleccionSinTerminar: careers.find(
                          (c) => c.tieneSeleccionSinTerminar === true,
                        ),
                      });
                    }}
                    value={careerValue?.carreraId}
                    options={careers
                      ?.filter((career) => career.periodo !== null)
                      ?.map((c) => ({
                        label: (
                          <CareersOptionsContainer>
                            <span>
                              <strong>{c.carreraNombre}</strong>
                            </span>
                            <span>
                              {`${enrollmentPage?.period_label || "Periodo"} ${c.periodo}, ${enrollmentPage?.date_from_label || "desde"} ${formatDate(
                                c.periodoFechaInicio,
                              )} ${enrollmentPage?.date_to_label || "hasta"} ${formatDate(c.periodoFechaFin)}`}
                            </span>
                          </CareersOptionsContainer>
                        ),
                        value: c.carreraId,
                      }))}
                  />
                </OptionsContainer>
              </FlexStyled>

              <div>{parse(translatedTerminos || (terminos ?? ""))}</div>

              {careers?.filter((career) => career.periodo !== null).length > 0 && (
                <Checkbox
                  onChange={(e) => setValue("aceptaTerminos", e.target.checked)}
                  defaultChecked={getValues().aceptaTerminos}
                >
                  <Text style={{ fontWeight: "bold" }}>{enrollmentPage?.accept_terms || "Acepto los términos"}</Text>
                </Checkbox>
              )}
              <br />
              <br />

              {userInfo?.saldoVencidoColones > 0.1 || userInfo?.saldoVencidoDolares > 0.1 ? (
                <WarningCard
                  size="small"
                  message={`${enrollmentPage?.messagepO || "Posee un saldo vencido de:"} ${
                    userInfo?.saldoVencidoColones > 0
                      ? `${FormatterUtils.number(userInfo.saldoVencidoColones?.toFixed(2))} colones`
                      : ""
                  }${
                    userInfo?.saldoVencidoColones > 0 && userInfo?.saldoVencidoDolares > 0
                      ? enrollmentPage?.and_connector || "y"
                      : ""
                  }${
                    userInfo?.saldoVencidoDolares > 0
                      ? `${FormatterUtils.number(userInfo.saldoVencidoDolares?.toFixed(2))} dólares`
                      : ""
                  }${enrollmentPage?.messagepT || "; al finalizar el proceso, deberá pagar este monto junto con los cargos asociados al mismo."}`}
                />
              ) : null}
            </>
          )}
        </Box>
        <Image src={IntroductionImg} width={285} alt={isLoadingContent ? <Skeleton.Input active size="default" style={{ width: 285 }} /> : (enrollmentPage?.register_ilustration || "Ilustración registro")} preview={false} />
      </IntroductionContainer>
    </RegisterCard>
  );
};

export default RegisterIntroduction;
